import React, { useState, useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Table,
    Input,
    Button,
    Badge,
    Pagination,
    PaginationItem,
    PaginationLink,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';
import { FaSearch, FaEdit, FaTrash, FaUser, FaFilter, FaSort, FaSortUp, FaSortDown, FaPlus, FaRegBuilding } from 'react-icons/fa';
import { CLIENT_FORM_PAGE } from '../../routes/routeConstants';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { clientActions } from '../../sagas/clientSlice';
import { formatPrice } from '../../helpers/utility';
import Swal from 'sweetalert2';

const ClientList = () => {

    const navigate = useNavigate();

    // États
    const { clients, loading } = useSelector(state => state.clients);
    const [filteredClients, setClients] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [clientsPerPage] = useState(4);
    const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState('tous');
    const { t } = useTranslation(); // Utilisation de i18next pour la traduction
    const dispatch = useDispatch();
    // Formatage MAD
    const formatMAD = (montant) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD',
            minimumFractionDigits: 2
        }).format(montant);
    };

    useEffect(() => {
        dispatch(clientActions.search({
                query: ""
            }));// Appel de l'action pour récupérer les clients    
    }, []);
    
    // Filtrage et recherche
    useEffect(() => {
        if (loading) return; // Ne pas exécuter si le chargement est en cours
        let result = clients;
        
        // Filtre par statut
        if (statusFilter !== 'tous') {
            result = result.filter(client => client.statut === statusFilter);
        }
        console.log(result);
        // Recherche
        if (searchTerm) {
            result = result.filter(client => {
                const clientName = client.typeClient === 'PARTICULIER' ? `${client.nom} ${client.prenom}` : client.entreprise;
                return clientName.toLowerCase().includes(searchTerm.toLowerCase()) || client.code.toString().includes(searchTerm);
            })
        }
        

        

        setClients(result);
        setCurrentPage(1);
    }, [searchTerm, statusFilter, clients]);

    // Tri
    const requestSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortedClients = clients ? [...clients].sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
            return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
    }) : [];

    // Pagination
    const indexOfLastClient = currentPage * clientsPerPage;
    const indexOfFirstClient = indexOfLastClient - clientsPerPage;
    sortedClients.slice(indexOfFirstClient, indexOfLastClient);
    const totalPages = Math.ceil(sortedClients.length / clientsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleDeleteClient = (clientId) => {
        Swal.fire({
            title: 'Êtes-vous sûr ?',
            text: "Le client sera désactivé et ne pourra plus être utilisé.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Oui, désactiver !',
            cancelButtonText: 'Annuler'
        }).then((result) => {
            if (result.isConfirmed) {
                let payload = {
                    id: clientId,
                    onSuccess: () => {
                        Swal.fire(
                            'Inactivé !',
                            'Le client a été désactivé avec succès.',
                            'success'
                        );
                        dispatch(clientActions.list()); // Recharger la liste des clients après la suppression
                    },
                    onError: () => {
                        Swal.fire(
                            'Erreur !',
                            'Une erreur s\'est produite lors de la désactivation.',
                            'error'
                        );
                    }
                }

                dispatch(clientActions.inactivate(payload));
            }
        });
    };

    const handleEdit = (client) => {
        navigate(CLIENT_FORM_PAGE, { state: { client } });
    };

    return (
        <Container fluid>
            <Row className="mb-4" >
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaRegBuilding className="text-primary me-2" />
                                {t('client.list')}
                            </h4>
                            <Button
                                color="primary"
                                onClick={() => navigate(CLIENT_FORM_PAGE)} // Go back to the previous page
                                className="d-flex align-items-center"
                            >
                                <FaPlus className="me-2" />
                                {t('client.newClient')}
                            </Button>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Card className="shadow-sm">
                <CardBody>
                    {/* Barre de recherche et filtres */}
                    <Row className="mb-4">
                        <Col md={10}>
                            <div className="input-group">
                                <Input
                                    type="text"
                                    placeholder="Rechercher un client..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="border-right-0"
                                />
                                <div className="input-group-append">
                                    <span className="input-group-text bg-white">
                                        <FaSearch />
                                    </span>
                                </div>
                            </div>
                        </Col>
                        <Col md={2} className="text-right">
                            <Dropdown
                                isOpen={filterDropdownOpen}
                                toggle={() => setFilterDropdownOpen(!filterDropdownOpen)}
                                className="d-inline-block ml-2"
                            >
                                <DropdownToggle caret color="light">
                                    <FaFilter className="me-1" />
                                    Filtre: {statusFilter === 'tous' ? 'Tous' : statusFilter}
                                </DropdownToggle>
                                <DropdownMenu right>
                                    <DropdownItem onClick={() => setStatusFilter('tous')}>Tous</DropdownItem>
                                    <DropdownItem onClick={() => setStatusFilter('actif')}>Actifs</DropdownItem>
                                    <DropdownItem onClick={() => setStatusFilter('bloqué')}>Bloqués</DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        </Col>
                    </Row>

                    {/* Tableau des clients */}
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead className="thead-light">
                                <tr>
                                    <th onClick={() => requestSort('code')} style={{ cursor: 'pointer' }}>
                                        Code {sortConfig.key === 'code' && (
                                            sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                                        )}
                                    </th>
                                    <th onClick={() => requestSort('nom')} style={{ cursor: 'pointer' }}>
                                        Nom {sortConfig.key === 'nom' && (
                                            sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                                        )}
                                    </th>

                                    <th>Contact</th>
                                    <th onClick={() => requestSort('ville')} style={{ cursor: 'pointer' }}>
                                        Ville {sortConfig.key === 'ville' && (
                                            sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />
                                        )}
                                    </th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredClients.length > 0 ? (
                                    filteredClients.map(client => (
                                        <tr key={client.id}>
                                            <td className="font-weight-bold">{client.code}</td>
                                            <td>
                                                <div>
                                                    {client.typeClient === 'PARTICULIER' ? client.nom + ' ' + client.prenom : client.entreprise}
                                                </div>
                                            </td>
                                            <td>
                                                <div>{client.telephone1}</div>
                                                <small className="text-muted">{client.email}</small>
                                            </td>
                                            <td>{client.ville}</td>
                                            
                                            <td>
                                                <Badge color={client.active ? 'success' : 'danger'} pill>
                                                    {client.active ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Button color="warning" size="sm" className="text-white" onClick={() => handleEdit(client)}>
                                                    <FaEdit />
                                                </Button>
                                                <Button color="danger" size="sm" className="text-white ms-2" onClick={() => handleDeleteClient(client.id)}>
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4">
                                            Aucun client trouvé
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="d-flex justify-content-center mt-4">
                            <Pagination>
                                <PaginationItem disabled={currentPage === 1}>
                                    <PaginationLink previous onClick={() => paginate(currentPage - 1)} />
                                </PaginationItem>

                                {[...Array(totalPages)].map((_, index) => (
                                    <PaginationItem active={index + 1 === currentPage} key={index}>
                                        <PaginationLink onClick={() => paginate(index + 1)}>
                                            {index + 1}
                                        </PaginationLink>
                                    </PaginationItem>
                                ))}

                                <PaginationItem disabled={currentPage === totalPages}>
                                    <PaginationLink next onClick={() => paginate(currentPage + 1)} />
                                </PaginationItem>
                            </Pagination>
                        </div>
                    )}

                    {/* Résumé */}
                    <div className="mt-3 text-muted">
                        Affichage de {indexOfFirstClient + 1} à {Math.min(indexOfLastClient, sortedClients.length)} sur {sortedClients.length} clients
                    </div>
                </CardBody>
            </Card>
        </Container>
    );
};

export default ClientList;